/** Deep link into a project's Takeoff drawing from elsewhere in the app (BOQ
 *  dim sheet sources, finished QuantisAI takeoff jobs). The intent is written
 *  to session storage as well as dispatched, because TakeoffView is lazily
 *  mounted: when the link fires from another view there is no listener yet,
 *  and the view reads the stored intent once its sheets have loaded. */

export const LINKED_SOURCE_STORAGE_KEY = 'quantis:takeoff-open-source';

export interface LinkedSourceDetail {
  projectId?: string;
  sheetId?: string;
  /** PDF page within the sheet's source file. Omitted means the sheet's own page. */
  page?: number;
  measurementId?: string;
  /** Open the Types inspector, where the AI suggestion review tray lives. */
  review?: boolean;
  createdAt?: number;
}

/** The page to move to after the sheet opens, or null to stay on the sheet's
 *  own page. Rejects anything outside the file so a stale or malformed link
 *  cannot strand the canvas on a page that does not exist. */
export function resolveLinkedSourcePage(
  requested: unknown,
  sheet: { source_page?: number | null; page_count?: number | null },
): number | null {
  if (typeof requested !== 'number' || !Number.isSafeInteger(requested) || requested < 1) return null;
  const pageCount = sheet.page_count ?? 0;
  if (pageCount > 0 && requested > pageCount) return null;
  return requested === (sheet.source_page ?? 1) ? null : requested;
}

/** Navigates to the project's Takeoff tab and, when a sheet is named, opens it.
 *  Without a sheet the Takeoff view opens on whatever it would show anyway. */
export function openTakeoffLinkedSource(link: Omit<LinkedSourceDetail, 'createdAt'> & { projectId: string }): void {
  const detail: LinkedSourceDetail = { ...link };
  if (link.sheetId) {
    try {
      sessionStorage.setItem(LINKED_SOURCE_STORAGE_KEY, JSON.stringify({ ...detail, createdAt: Date.now() }));
    } catch {
      // Navigation still works if session storage is unavailable.
    }
  }
  window.dispatchEvent(new CustomEvent('app:navigate', {
    detail: { view: 'project-detail', projectId: link.projectId, projectTab: 'takeoff' },
  }));
  if (!link.sheetId) return;
  // An already-mounted TakeoffView misses nothing from session storage, but it
  // only re-reads that on a sheet list change; the event reaches it directly.
  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent('takeoff:open-linked-source', { detail }));
  }, 120);
}
